import { useEffect, useState } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { AppLoader } from '@/components/AppLoader';
import { AppShell } from '@/components/layout/AppShell';
import { AuthForm } from '@/components/auth/AuthForm';
import NotFound from '@/pages/NotFound';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

export const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('profile_type')
          .eq('user_id', user.id)
          .single();
        
        setIsAdmin(data?.profile_type === 'admin');
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading]);

  // Show loader while checking auth and admin status
  if (authLoading || loading) {
    return <AppLoader />;
  }

  // If not authenticated, show login form
  if (!user) {
    return <AuthForm />;
  }

  // If authenticated but not admin, show 404
  if (!isAdmin) {
    return <NotFound />;
  }

  return <AppShell>{children}</AppShell>;
};